import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  approveClaim,
  getMyClaimOptions,
  listApprovals,
  listMyClaims,
  markClaimReimbursed,
  rejectClaim,
  submitClaim,
  withdrawClaim,
} from '@/api/services/expense-claims.service'
import { getClaimErrorMessage } from '../utils/claimDisplay'
import type { ApprovalsQuery, MyClaimsQuery, SubmitClaimDto } from '../types/expenseClaim.types'

/**
 * Every claim query lives under ['expense-claims']; mutations invalidate that
 * param-less prefix so both the claimant's list and the inbox refresh.
 */
export const expenseClaimKeys = {
  all: ['expense-claims'] as const,
  options: () => ['expense-claims', 'options'] as const,
  mine: (params: MyClaimsQuery) => ['expense-claims', 'mine', params] as const,
  approvals: (params: ApprovalsQuery) => ['expense-claims', 'approvals', params] as const,
}

export function useMyClaimOptions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: expenseClaimKeys.options(),
    queryFn: getMyClaimOptions,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useMyClaims(params: MyClaimsQuery) {
  return useQuery({
    queryKey: expenseClaimKeys.mine(params),
    queryFn: () => listMyClaims(params),
    staleTime: 30 * 1000,
  })
}

export function useClaimApprovals(params: ApprovalsQuery) {
  return useQuery({
    queryKey: expenseClaimKeys.approvals(params),
    queryFn: () => listApprovals(params),
    staleTime: 30 * 1000,
  })
}

function useClaimMutation<TVars>(fn: (vars: TVars) => Promise<unknown>, successMessage: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: expenseClaimKeys.all })
      toast.success(successMessage)
    },
    onError: (error) => {
      toast.error(getClaimErrorMessage(error))
    },
  })
}

export function useSubmitClaim() {
  return useClaimMutation((body: SubmitClaimDto) => submitClaim(body), 'Expense claim submitted')
}

export function useWithdrawClaim() {
  return useClaimMutation((id: string) => withdrawClaim(id), 'Expense claim withdrawn')
}

export function useApproveClaim() {
  return useClaimMutation(
    ({ id, note }: { id: string; note?: string }) => approveClaim(id, note),
    'Claim approved'
  )
}

export function useRejectClaim() {
  return useClaimMutation(
    ({ id, reason }: { id: string; reason: string }) => rejectClaim(id, reason),
    'Claim rejected'
  )
}

export function useMarkClaimReimbursed() {
  return useClaimMutation(
    ({ id, note }: { id: string; note: string }) => markClaimReimbursed(id, note),
    'Claim marked as reimbursed outside payroll'
  )
}
